import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lightning, X } from '@phosphor-icons/react';
import { SPEED_RUN_EVENT, endSpeedRun, inSpeedRun, startSpeedRun } from '@/lib/speedRun';
import { useAuth } from '@/contexts/AuthContext';
import { favoritesApi, speedRunApi } from '@/api/client';
import { cleanUpRun, rememberFavoritesBefore } from '@/lib/speedRunLens.core';

/****************
 * THE ADMIN'S SPEED RUN: the side menu entry that starts it and, during the
 * run, leaves it (live review #143, lib/speedRun.ts). Guillaume 11-01: no bar
 * across the top, no "Back to Admin" stripe; the "Speed run" entry in the side
 * menu becomes the way out. Both reload the app on "/" so every screen starts
 * again as for a newcomer, then as the admin again.
 ****************/
export async function beginSpeedRun(): Promise<void> {
  // The favourites he already has, read before the run hides them: leaving keeps those.
  const before = await favoritesApi.list().catch(() => null);
  startSpeedRun(null);
  if (before) rememberFavoritesBefore(before);
  window.location.assign('/');
}

/** Leaving throws away what the run made (strategies, backtests, paper sessions, new favourites). */
export async function leaveSpeedRun(): Promise<void> {
  const result = await cleanUpRun(speedRunApi).catch(() => null);
  if (result) console.info('[speed run] cleanup', result);
  endSpeedRun();
  window.location.assign('/');
}

/** True while a speed run is on, kept in sync with its start and end. */
export function useInSpeedRun(): boolean {
  const [on, setOn] = useState(inSpeedRun());
  useEffect(() => {
    const sync = () => setOn(inSpeedRun());
    window.addEventListener(SPEED_RUN_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener(SPEED_RUN_EVENT, sync); window.removeEventListener('storage', sync); };
  }, []);
  return on;
}

/**
 * The admin menu entry: "Speed run" to start, "Leave speed run" (with an X)
 * during a run. Renders nothing for anyone but a real admin (during the run the
 * admin sees the app as a Free account, so isAdmin is false: isRealAdmin is not).
 */
export function SpeedRunButton({ className, showLabel = true }: { className?: string; showLabel?: boolean }) {
  const { t } = useTranslation('common');
  const { isRealAdmin } = useAuth();
  const on = useInSpeedRun();
  if (!isRealAdmin) return null;
  const base = className ?? 'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-orange-300 hover:bg-white/[0.05]';
  if (on) {
    return (
      <button type="button" onClick={() => { void leaveSpeedRun(); }} title={t('speedRun.leaveHint')} aria-label={t('speedRun.leaveRun')}
        data-testid="speed-run-leave"
        className={`${base} border border-orange-400/60 bg-orange-500/15`}>
        <X size={16} weight="bold" className="shrink-0" />
        {showLabel && t('speedRun.leaveRun')}
      </button>
    );
  }
  return (
    <button type="button" onClick={() => { void beginSpeedRun(); }} title={t('speedRun.startHint')} aria-label={t('speedRun.start')} className={base}>
      <Lightning size={16} weight="fill" className="shrink-0" />
      {showLabel && t('speedRun.start')}
    </button>
  );
}
